// SPDX-License-Identifier: Apache-2.0

// SlackSetupPanel (Iris). 3-step Slack stealth-cookie flow.
//
//   1. extract  — paste xoxc + xoxd from slack.com DevTools cookies.
//   2. validate — POST /api/integrations/slack/test (auth.test).
//   3. save     — POST /api/integrations/slack/configure, scrub inputs.
//
// Tokens live ONLY in transient signals — never localStorage, never the audit
// body. Inputs zeroed after a successful save.
//
// Inputs use type="text" + autocomplete="off" (NOT type="password" — that
// triggers Chrome's autofill prompts). A show/hide toggle styles the input
// with `-webkit-text-security: disc` when off.

import { Show, createMemo, createSignal, type JSX } from "solid-js";
import { ActionRow, ErrorBox, Stepper, SuccessBox } from "./WizardStep";

const MCP_BASE = "http://localhost:5417/api/integrations";
const STEP_LABELS = ["extract cookies", "validate", "save & enable"] as const;

type FetchState = "idle" | "pending" | "ok" | "error";

export interface SlackSetupPanelProps {
  step: number;
  setStep: (n: number) => void;
  onDone: () => void;
}

export default function SlackSetupPanel(
  props: SlackSetupPanelProps
): JSX.Element {
  const [xoxc, setXoxc] = createSignal("");
  const [xoxd, setXoxd] = createSignal("");
  const [reveal, setReveal] = createSignal(false);
  const [vState, setVState] = createSignal<FetchState>("idle");
  const [vMsg, setVMsg] = createSignal<{ team?: string; user?: string; error?: string }>({});
  const [sState, setSState] = createSignal<FetchState>("idle");
  const [sMsg, setSMsg] = createSignal<{ tool_count?: number; error?: string; warning?: string }>({});

  const wellFormed = createMemo(
    () =>
      xoxc().startsWith("xoxc-") &&
      xoxc().length > 6 &&
      xoxd().startsWith("xoxd-") &&
      xoxd().length > 6
  );
  const maskStyle = () => (reveal() ? {} : { "-webkit-text-security": "disc" as const });

  const validate = async () => {
    if (!wellFormed()) return;
    setVState("pending");
    setVMsg({});
    try {
      const res = await fetch(`${MCP_BASE}/slack/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ xoxc: xoxc(), xoxd: xoxd() }),
      });
      const b = (await res.json()) as { ok: boolean; team?: string; user?: string; error?: string };
      if (b.ok) { setVState("ok"); setVMsg({ team: b.team, user: b.user }); }
      else { setVState("error"); setVMsg({ error: b.error ?? "validation failed" }); }
    } catch (err) {
      setVState("error");
      setVMsg({ error: (err as Error).message });
    }
  };

  const save = async () => {
    if (vState() !== "ok") return;
    setSState("pending");
    setSMsg({});
    try {
      const res = await fetch(`${MCP_BASE}/slack/configure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ xoxc: xoxc(), xoxd: xoxd() }),
      });
      const b = (await res.json()) as { ok: boolean; tool_count?: number; warning?: string; error?: string };
      if (b.ok) {
        setSState("ok");
        setSMsg({ tool_count: b.tool_count ?? 0, warning: b.warning });
        setXoxc(""); setXoxd("");
        props.onDone();
      } else { setSState("error"); setSMsg({ error: b.error ?? "save failed" }); }
    } catch (err) {
      setSState("error");
      setSMsg({ error: (err as Error).message });
    }
  };

  return (
    <section class="iris-wiz__panel" data-testid="comms-wizard-slack-panel">
      <Stepper total={3} current={props.step} labels={STEP_LABELS} />

      <Show when={props.step === 1}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Open Slack and copy two cookies</h3>
          <p class="iris-wiz__copy">
            The local MCP child uses your own browser session — tokens never
            leave this machine.
          </p>
          <ol class="iris-wiz__instructions">
            <li>Sign in at <code>https://app.slack.com</code> in this browser.</li>
            <li>DevTools → <strong>Application → Cookies → app.slack.com</strong>; copy <code>d</code> (starts <code>xoxd-</code>).</li>
            <li>DevTools → <strong>Network</strong>, refresh, click any <code>api.slack.com</code> call, copy form-field <code>token</code> (starts <code>xoxc-</code>).</li>
          </ol>
          <label class="iris-wiz__field">
            <span class="iris-wiz__label">xoxc token</span>
            <input
              type="text" class="iris-wiz__input" data-testid="slack-xoxc-input"
              placeholder="xoxc-..." value={xoxc()} autocomplete="off" spellcheck={false}
              style={maskStyle()} onInput={(e) => setXoxc(e.currentTarget.value)}
            />
          </label>
          <label class="iris-wiz__field">
            <span class="iris-wiz__label">xoxd cookie</span>
            <input
              type="text" class="iris-wiz__input" data-testid="slack-xoxd-input"
              placeholder="xoxd-..." value={xoxd()} autocomplete="off" spellcheck={false}
              style={maskStyle()} onInput={(e) => setXoxd(e.currentTarget.value)}
            />
          </label>
          <label class="iris-wiz__hint" style={{ cursor: "pointer" }}>
            <input
              type="checkbox" data-testid="slack-reveal-toggle"
              checked={reveal()} onChange={(e) => setReveal(e.currentTarget.checked)}
            />{" "}show tokens
          </label>
          <Show when={!wellFormed() && (xoxc().length > 0 || xoxd().length > 0)}>
            <p class="iris-wiz__hint iris-wiz__hint--warn" data-testid="slack-shape-warn">
              both tokens must start with <code>xoxc-</code> / <code>xoxd-</code>
            </p>
          </Show>
          <ActionRow
            primaryLabel="continue →"
            primaryTestId="slack-step1-next"
            primaryDisabled={!wellFormed()}
            onPrimary={() => props.setStep(2)}
          />
        </div>
      </Show>

      <Show when={props.step === 2}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Test the cookie pair</h3>
          <p class="iris-wiz__copy">
            We call Slack's <code>auth.test</code>. If it returns the workspace
            you expect, we're good to save.
          </p>
          <ActionRow
            onBack={() => props.setStep(1)}
            primaryLabel={vState() === "pending" ? "testing…" : "test connection"}
            primaryTestId="slack-validate-btn"
            primaryDisabled={!wellFormed() || vState() === "pending"}
            onPrimary={() => void validate()}
          />
          <Show when={vState() === "ok"}>
            <SuccessBox testId="slack-validate-ok">
              <strong>Connected.</strong> workspace <code>{vMsg().team}</code>,
              user <code>{vMsg().user}</code>.
            </SuccessBox>
            <ActionRow
              primaryLabel="continue →"
              primaryTestId="slack-step2-next"
              onPrimary={() => props.setStep(3)}
            />
          </Show>
          <Show when={vState() === "error"}>
            <ErrorBox testId="slack-validate-err">
              <strong>Slack rejected the cookies:</strong> <code>{vMsg().error}</code>
            </ErrorBox>
          </Show>
        </div>
      </Show>

      <Show when={props.step === 3}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Save & enable Slack tools</h3>
          <p class="iris-wiz__copy">
            Writing to <code>~/.tether-brain/integrations.json</code>{" "}
            (<code>chmod 0600</code>) and respawning the slack MCP child.
          </p>
          <ActionRow
            onBack={() => props.setStep(2)}
            primaryLabel={
              sState() === "pending" ? "saving…" : sState() === "ok" ? "saved ✓" : "save & enable"
            }
            primaryTestId="slack-save-btn"
            primaryDisabled={sState() === "pending" || sState() === "ok"}
            onPrimary={() => void save()}
          />
          <Show when={sState() === "ok"}>
            <SuccessBox testId="slack-save-ok">
              <strong>{sMsg().tool_count ?? 0} slack.* tools now reachable from chat.</strong>
              <Show when={sMsg().warning}>
                <p class="iris-wiz__hint iris-wiz__hint--warn">{sMsg().warning}</p>
              </Show>
            </SuccessBox>
          </Show>
          <Show when={sState() === "error"}>
            <ErrorBox testId="slack-save-err">{sMsg().error}</ErrorBox>
          </Show>
        </div>
      </Show>
    </section>
  );
}
